/* global fetch */
/* global localStorage */
import React, { useCallback, useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import get from 'lodash/get'
import cn from 'classnames'
// import { addToCart } from '../../../redux/actions/cart'
import { isCartBusy } from '../../../redux/slices/cart'
import { getCartBuyButtonLabels, getProductDefaultsByHandle } from '../../../redux/slices/content'
import { openCartDialog } from '../../../redux/slices/layout'
import { createUseStyles } from 'react-jss'
import theme from '../../../style/theme'
import { useHoverCursor } from '../../Cursor'
import { vw } from '../../../style/vw'

const BuyButton = ({ product, variantId, className, text, quantity, setQuantity, sellingPlanId, selectedPrice, isSubscription, isMainProduct = false, ...rest }) => {
  const dispatch = useDispatch()
  const busy = useSelector(isCartBusy)
  const labels = useSelector(getCartBuyButtonLabels)
  // Debug logging for slug usage
  // console.log('🛒 BuyButton: Product slug data:', {
  //   productSlug: get(product, ['slug']),
  //   productTitle: product?.title || product?.store?.title,
  //   productId: product?.store?.id
  // })

  const addToCartLabel = get(useSelector(getProductDefaultsByHandle), [get(product, ['slug']), 'add_to_cart_label'], labels.addToCartLabel)
  const initialText = !get(product, ['availableForSale'], false) ? labels.outOfStockLabel : (text || addToCartLabel)

  // Debug logging for BuyButton
  // console.log('🛒 BuyButton: Availability check:', {
  //   productSlug: get(product, ['slug']),
  //   availableForSale: get(product, ['availableForSale'], false),
  //   initialText,
  //   outOfStockLabel: labels.outOfStockLabel,
  //   addToCartLabel: text || addToCartLabel
  // })

  const [state, setState] = useState(initialText)
  // const [quantity, setQuantity] = useState(1)
  const buttonRef = useRef()
  const incrementRef = useRef()
  const decrementRef = useRef()
  const clicked = useRef()
  const classes = useStyles()
  // const isMobile = useSelector(isCurrentBreakpointMobile)
  const [isAddedToCart, setIsAddedToCart] = useState(false)
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [stockError, setStockError] = useState(null)

  useHoverCursor(buttonRef)
  useHoverCursor(incrementRef)
  useHoverCursor(decrementRef)

  const disable = state !== initialText || !product.availableForSale

  const handleIncrement = useCallback(() => {
    if (quantity < 99) setQuantity(quantity + 1)
  }, [quantity, setQuantity])

  const handleDecrement = useCallback(() => {
    if (quantity > 1) setQuantity(quantity - 1)
  }, [quantity, setQuantity])

  // const handleCartClick = useCallback(() => {
  //   dispatch(toggleCartDialog())
  // }, [dispatch])

  // const handleCartClick = (e) => {
  //   e.preventDefault()
  //   const cartId = localStorage.getItem('cartId')
  //   let cartUrl = 'https://https://staging.discoveredfoods.com//pages/food-cart'

  //   if (cartId) {
  //     cartUrl += `?cartId=${cartId}`

  //     // Append selling plan IDs as query parameters
  //     const cartLines = JSON.parse(localStorage.getItem('cartLines') || '[]')
  //     cartLines.forEach((line, index) => {
  //       // Ensure that each line item ID and selling plan ID is unique and correctly paired
  //       cartUrl += `&spId${index}=${line.sellingPlanId}&lineId${index}=${encodeURIComponent(line.lineItemId)}`
  //     })
  //   }

  //   // console.log(cartUrl)
  //   window.location.href = cartUrl
  // }

  const onClick = useCallback(async () => {
    if (!disable) {
      setIsAddingToCart(true)
      clicked.current = true

      // console.log('🛒 Adding to cart:')
      // console.log('  - Variant ID:', variantId)
      // console.log('  - Quantity:', quantity)
      // console.log('  - Selling Plan ID (received from SubscriptionWidget):', sellingPlanId)
      // console.log('  - Selling Plan ID type:', typeof sellingPlanId)
      // console.log('  - Is this a Shopify selling plan ID?', sellingPlanId &&
      //   (sellingPlanId.toString().length <= 12 || sellingPlanId.includes('gid://')))

      // Format IDs for Shopify Storefront API
      // Check if variantId already contains the shopify prefix to avoid double-prefixing
      let formattedVariantId
      if (variantId.includes('gid://shopify/ProductVariant/')) {
        formattedVariantId = variantId
      } else if (variantId.startsWith('shopifyProductVariant-')) {
        // Remove the prefix and use just the numeric ID
        const numericId = variantId.replace('shopifyProductVariant-', '')
        formattedVariantId = `gid://shopify/ProductVariant/${numericId}`
      } else {
        formattedVariantId = `gid://shopify/ProductVariant/${variantId}`
      }

      // Base64 encode the IDs for Shopify Storefront API
      const base64VariantId = Buffer.from(formattedVariantId).toString('base64') // Only prepare selling plan ID if one is selected
      let base64SellingPlanId = null
      let formattedSellingPlanId = null
      if (sellingPlanId) {
        // Handle different selling plan ID formats
        if (sellingPlanId.includes('gid://shopify/SellingPlan/')) {
          formattedSellingPlanId = sellingPlanId
        } else if (typeof sellingPlanId === 'string' && sellingPlanId.length > 10) {
          // This is likely already a Shopify selling plan ID
          formattedSellingPlanId = `gid://shopify/SellingPlan/${sellingPlanId}`
        } else {
          // For test IDs like 'test123', treat them as Shopify selling plan IDs
          formattedSellingPlanId = `gid://shopify/SellingPlan/${sellingPlanId}`
        }
        base64SellingPlanId = Buffer.from(formattedSellingPlanId).toString('base64')
      }

      // console.log('  - Formatted variant ID:', formattedVariantId)
      // console.log('  - Base64 variant ID:', base64VariantId)
      // if (sellingPlanId) {
      //   console.log('  - Formatted selling plan ID:', formattedSellingPlanId)
      //   console.log('  - Base64 selling plan ID:', base64SellingPlanId)
      // }

      const lineItem = {
        merchandiseId: base64VariantId,
        quantity: quantity
      }

      if (base64SellingPlanId) {
        lineItem.sellingPlanId = base64SellingPlanId
      }

      const existingCartId = localStorage.getItem('cartId')

      const data = {
        cartId: existingCartId,
        lines: [lineItem]
      }

      try {
        // console.log('🔄 Sending API request to add item to cart:', data)

        const response = await fetch('https://api-server.discoveredfoods.com/api/addToCart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        })

        // Handle response
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }
        const responseData = await response.json()
        // console.log('✅ Response from server:', responseData)

        // Check for specific errors in the response structure
        if (responseData?.data?.cartLinesAdd?.userErrors?.length) {
          console.warn('🚨 Shopify userErrors:', responseData.data.cartLinesAdd.userErrors)
          responseData.data.cartLinesAdd.userErrors.forEach((error, index) => {
            console.warn(`🚨 Error ${index + 1}:`, error)
          })
        }

        const userErrors = responseData?.data?.cartLinesAdd?.userErrors || []
        if (userErrors.length > 0) {
          const firstErrorMsg = userErrors[0]?.message || 'There was a problem adding this item.'
          const errorMsgLower = firstErrorMsg.toLowerCase()
          const isInvalidCart = errorMsgLower.includes('cart does not exist') ||
            errorMsgLower.includes('cart is invalid') ||
            errorMsgLower.includes('invalid cart')

          if (isInvalidCart) {
            // The stored cartId has expired on Shopify — clear it and retry with no cartId
            console.log('🧹 BuyButton: Invalid cart detected, clearing localStorage and retrying...')
            localStorage.removeItem('cartId')
            localStorage.removeItem('cartLines')
            localStorage.removeItem('discoveredFoodsCart1')

            // Retry with no cartId so Shopify creates a fresh cart
            const retryData = { cartId: null, lines: [lineItem] }
            try {
              const retryResponse = await fetch('https://api-server.discoveredfoods.com/api/addToCart', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(retryData)
              })
              if (retryResponse.ok) {
                const retryResponseData = await retryResponse.json()
                const retryErrors = retryResponseData?.data?.cartLinesAdd?.userErrors || []
                if (retryErrors.length > 0) {
                  setStockError(retryErrors[0]?.message || 'There was a problem adding this item.')
                  setIsAddingToCart(false)
                  return
                }
                // Success on retry — treat as the main response
                if (retryResponseData?.data?.cartLinesAdd?.cart) {
                  const newCartId = retryResponseData.data.cartLinesAdd.cart.id
                  localStorage.setItem('cartId', newCartId)
                  const freshCartLines = []
                  retryResponseData.data.cartLinesAdd.cart.lines.edges.forEach(edge => {
                    freshCartLines.push({
                      lineItemId: edge.node.id,
                      variantId: variantId,
                      quantity: edge.node.quantity,
                      sellingPlanId: sellingPlanId,
                      isSubscription: !!sellingPlanId
                    })
                  })
                  localStorage.setItem('cartLines', JSON.stringify(freshCartLines))
                  dispatch(openCartDialog())
                  setIsAddedToCart(true)
                  setTimeout(() => setIsAddedToCart(false), 100000)
                }
                setIsAddingToCart(false)
                return
              }
            } catch (retryError) {
              console.error('❌ BuyButton: Retry after invalid cart error failed:', retryError)
            }
          }

          setStockError(firstErrorMsg)
          setIsAddingToCart(false)
          return
        }

        const existingCartLines = JSON.parse(localStorage.getItem('cartLines')) || []

        if (responseData?.data?.cartLinesAdd?.cart) {
          const newCartId = responseData.data.cartLinesAdd.cart.id
          localStorage.setItem('cartId', newCartId)

          responseData.data.cartLinesAdd.cart.lines.edges.forEach(edge => {
            // Get selling plan data if it exists
            const sellingPlanData = edge.node.sellingPlanAllocation?.sellingPlan

            const newCartLine = {
              lineItemId: edge.node.id,
              variantId: variantId,
              quantity: edge.node.quantity,
              sellingPlanId: sellingPlanId,
              isSubscription: !!sellingPlanId
            }

            // Add selling plan details if available
            if (sellingPlanData) {
              newCartLine.sellingPlanName = sellingPlanData.name
              newCartLine.sellingPlanDescription = sellingPlanData.description
            }

            const existingLineIndex = existingCartLines.findIndex(line => line.lineItemId === newCartLine.lineItemId)

            if (existingLineIndex === -1) {
              existingCartLines.push(newCartLine)
            } else {
              existingCartLines[existingLineIndex] = {
                ...existingCartLines[existingLineIndex],
                quantity: edge.node.quantity
              }
            }
          })

          // Store the updated array back in localStorage
          localStorage.setItem('cartLines', JSON.stringify(existingCartLines))

          dispatch(openCartDialog())

          setIsAddedToCart(true)
          setTimeout(() => setIsAddedToCart(false), 100000)
        }
      } catch (error) {
        console.error('❌ Error adding to cart:', error)
      } finally {
        setIsAddingToCart(false)
      }
    }
  }, [disable, variantId, quantity, sellingPlanId, dispatch])

  useEffect(() => {
    const cartLines = JSON.parse(localStorage.getItem('cartLines')) || []
    const validCartLines = cartLines.filter(line => line.variantId)
    if (validCartLines.length !== cartLines.length) {
      localStorage.setItem('cartLines', JSON.stringify(validCartLines))
    }
  }, [])

  useEffect(() => {
    if (stockError) {
      const timer = setTimeout(() => {
        setStockError(null)
      }, 10000)

      return () => clearTimeout(timer)
    }
  }, [stockError])

  useEffect(() => {
    if (clicked.current) {
      if (busy) {
        // This locks the width so that it does not change when the text changes
        setState(labels.addingToCartLabel)
      } else {
        setState(labels.addedToCartLabel)
        setState(initialText)
        clicked.current = false
        // On mobile we may have  a popup that show when they add the item to the cart
        // and this will interfere with the card dialog if we try and open it
        dispatch(openCartDialog())
      }
    }
  }, [busy, labels, initialText, dispatch])

  // When isAddedToCart changes to true, set it back to false after 3 seconds
  useEffect(() => {
    if (isAddedToCart) {
      const timer = setTimeout(() => {
        setIsAddedToCart(false)
      }, 600000)

      return () => clearTimeout(timer) // Clear the timer if the component unmounts
    }
  }, [isAddedToCart])

  return (
    <>
      <div className={cn(classes.container, className)}>
        <div className={classes.quantitySelector}>
          <button className={classes.adjustButton} onClick={handleDecrement} aria-label='Decrement Product'>
            <span>-</span>
          </button>
          <span className={classes.quantityLabel}>{quantity}</span>
          <button className={classes.adjustButton} onClick={handleIncrement} aria-label='Increment Product'>
            <span>+</span>
          </button>
        </div>
        <button
          onClick={onClick}
          className={cn(classes.button, { disable })}
          ref={buttonRef}
          {...rest}
        >
          <div className={cn(classes.buttonText, classes.buyText)}>
            {isMainProduct && selectedPrice && !isNaN(selectedPrice) ? (
              <>
                {initialText} - ${Number(selectedPrice).toFixed(2)}
              </>
            ) : (
              initialText
            )}
          </div>
          {isAddingToCart && (
            <div className={classes.loader1}>
              <div className='ripple' />
            </div>
          )}
        </button>
      </div>
      {stockError && (
        <div className={classes.messageContainer}>
          <div className={classes.errorMessage}>
            {stockError}
          </div>
        </div>
      )}
      {/* <div className={classes.messageContainer}>
        {stockError ? (
          <div className={classes.addedtocarttxt}>
            <p style={{ color: '#d9534f' }}>{stockError}</p>
          </div>
        ) : isAddedToCart ? (
          <div className={classes.addedtocarttxt}>
            <p>Product Added to Cart <button onClick={handleCartClick}>View Cart</button></p>
          </div>
        ) : null}
      </div> */}
    </>
  )
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    border: [1, 'solid', theme.colors.primary],
    borderTop: 0
  },
  messageContainer: {
    width: '100%',
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 10
  },
  errorMessage: {
    display: 'block',
    fontWeight: '400',
    color: theme.colors.error,
    textAlign: 'center',
    fontSize: vw(14),
    lineHeight: 'calc(22/18)',
    padding: [9.5, 32],
    border: `1px solid ${theme.colors.error}`,
    [theme.breakpoints.up('md')]: {
      fontSize: vw(14, 'desktop')
    }
  },
  quantitySelector: {
    display: 'flex',
    alignItems: 'stretch'
  },
  quantityButton: {
    color: theme.colors.primary,
    minWidth: 37,
    minHeight: 37,
    background: 'none',
    outline: 'none',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: vw(14, 'md'),
    [theme.breakpoints.up('md')]: {
      fontSize: vw(14, 'desktop')
    }

  },
  quantityLabel: {
    composes: '$quantityButton',
    border: [0, 'solid', theme.colors.primary],
    borderLeft: 'none',
    borderRight: 'none',
    margin: 0
  },
  adjustButton: {
    composes: '$quantityButton',
    border: [0, 'solid', theme.colors.primary],
    cursor: 'pointer',
    '&:hover': {
      opacity: 1
    }
  },
  button: {
    display: 'block',
    minWidth: 96,
    textDecoration: 'none',
    padding: 0,
    margin: '0 auto',
    position: 'relative',
    cursor: 'pointer',
    width: '100%',
    border: [0, 'solid', theme.colors.primary],
    borderLeft: [1, 'solid', theme.colors.primary],
    borderColor: 'currentColor',
    color: 'currentColor',
    background: 'transparent',

    '&:hover': {
      backgroundColor: '#000',
      color: '#fff'
    },

    '&.disable': {
      opacity: 0.2,
      cursor: 'not-allowed'
    }
  },
  buttonText: {
    display: 'block',
    fontFamily: theme.fonts.body,
    fontWeight: theme.fonts.bodyFontWeight,
    fontSize: vw(14),
    lineHeight: 'calc(22/18)',
    textAlign: 'center',
    padding: [9.5, 32],

    [theme.breakpoints.up('md')]: {
      fontSize: vw(14, 'desktop')
    }
  },
  buyText: {
    composes: '$buttonText',
    '&.hide': { visibility: 'hidden' }
  },
  buyingText: {
    composes: '$buttonText',
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    textAlign: 'center',
    transition: 'none',
    '&.hide': {
      display: 'none'
    }
  },
  // Loader styles
  loader1: {
    margin: '0px',
    boxSizing: 'border-box',
    height: '32px',
    width: '32px',
    position: 'absolute',
    top: '50%',
    right: '-16px',
    zIndex: 10,
    '& .ripple': {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: '170px',
      right: '170px',
      margin: 'auto',
      height: '32px',
      width: '32px',
      '&:before, &:after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        boxSizing: 'border-box',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        margin: 'auto',
        height: '32px',
        width: '32px',
        border: '2px solid #38524F', // Replaced $border with its value
        borderRadius: '50%',
        opacity: 1,
        animation: '$loader1Ripple 3s cubic-bezier(0.075, 0.82, 0.165, 1) infinite'
      },
      '&:after': {
        animation: '$loader1Ripple2 3s cubic-bezier(0.075, 0.82, 0.165, 1) 0.5s infinite'
      }
    }
  },
  '@keyframes loader1Ripple': {
    '0%': {
      transform: 'translate3d(0, 0, 0) scale(0)',
      opacity: 1
    },
    '20%': {
      opacity: 1
    },
    '100%': {
      transform: 'translate3d(0, 0, 0) scale(1.5)',
      opacity: 0
    }
  },
  '@keyframes loader1Ripple2': {
    '0%': {
      transform: 'translate3d(0, 0, 0) scale(0)',
      opacity: 1
    },
    '20%': {
      opacity: 1
    },
    '100%': {
      transform: 'translate3d(0, 0, 0) scale(1)',
      opacity: 0
    }
  }
})

export default BuyButton
